// Numbers for the gates Overview: one game's gate data reduced to what the comparison needs,
// limited to the time window (offsets from each game's start), plus findings across the games.
import React from 'react';
import moment from 'moment';
import {
  buildGateStats, combinedCountSeries, intervalFlow, clockToMinute, minuteToClock, isLongQueue, QUEUE_TARGET,
} from './gatesMetrics';
import { peakStretch } from './gatesInsights';
import { formatDuration } from '../Concession/live/liveMetrics';

const MIN_EXIT_SHARE = 0.05;

// "about" times are rounded to 5 minutes: they summarise several games, so minute precision would mislead
const round5 = (m) => Math.round(m / 5) * 5;
const durationText = (m) => (m >= 60 ? formatDuration(m) : `${m} min`);

const relativeToStart = (minutes) => {
  const m = round5(Math.abs(minutes));
  if (m < 5) return 'right at game start';
  return minutes < 0 ? `${durationText(m)} before game start` : `${durationText(m)} after game start`;
};

// A range of offsets: "15–45 min before game start", or across game start when it straddles it
const offsetRange = (lo, hi) => {
  const a = round5(lo);
  const b = round5(hi);
  if (a < 0 && b > 0) return `between ${relativeToStart(a)} and ${relativeToStart(b)}`;
  const near = Math.min(Math.abs(a), Math.abs(b));
  const far = Math.max(Math.abs(a), Math.abs(b));
  const side = b <= 0 ? 'before' : 'after';
  return far < 60 ? `between ${near} and ${far} min ${side} game start` : `between ${durationText(near)} and ${durationText(far)} ${side} game start`;
};

// Middle of a quarter-hour, relative to game start (minutes; negative = before)
export const midOffset = (slot, startMinute) => (slot.start + slot.end) / 2 - startMinute;

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);
const avg = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

// One game: gates = the transformed gate list from fetchGatesDay; window = { startOffset, endOffset }
export const buildGateGameMetrics = ({ gates, date, gameDate, window }) => {
  const dateKey = moment(date).format('YYYY-MM-DD');
  const startMinute = clockToMinute(gameDate?.gameStartTime);
  const gatesOpenMinute = clockToMinute(gameDate?.gatesOpen);
  const range = startMinute != null
    ? { start: startMinute + window.startOffset, end: startMinute + window.endOffset }
    : null;

  const stats = gates.map((g) => buildGateStats(g, dateKey, null, range));
  const combined = combinedCountSeries(stats.map((s) => s.counts));
  const inRange = (m) => !range || (m >= range.start && m < range.end);
  const flow = intervalFlow(combined, 1).filter((b) => inRange(b.start));

  const entrances = stats.reduce((sum, s) => sum + s.entrances, 0);
  const exits = stats.reduce((sum, s) => sum + s.exits, 0);
  const rush = peakStretch(flow, 'in');
  const exitWave = exits >= 30 && exits >= entrances * MIN_EXIT_SHARE ? peakStretch(flow, 'out') : null;

  const perGate = stats.map((s) => ({
    id: s.gate.id,
    label: s.label,
    imageURL: s.gate.imageURL,
    entrances: s.entrances,
    exits: s.exits,
    queuePeak: s.queuePeak,
    longMinutes: s.queue.filter((p) => inRange(p.minute) && isLongQueue(p.queueTime)).length,
  }));
  const peakGate = perGate.reduce((top, g) => (g.queuePeak && (!top || g.queuePeak.value > top.queuePeak.value) ? g : top), null);
  const busiest = perGate.reduce((top, g) => (!top || g.entrances > top.entrances ? g : top), null);

  // Average queue across gates, minute by minute (gates with a reading that minute)
  const queueByMinute = new Map();
  stats.forEach((s) => s.queue.forEach((p) => {
    if (!inRange(p.minute)) return;
    const b = queueByMinute.get(p.minute) || { sum: 0, n: 0 };
    b.sum += p.queueTime;
    b.n += 1;
    queueByMinute.set(p.minute, b);
  }));
  const queuePoints = [...queueByMinute.entries()].sort((a, b) => a[0] - b[0]).map(([m, b]) => ({ m, v: b.sum / b.n }));

  const before = startMinute != null ? flow.filter((b) => b.start < startMinute).reduce((sum, b) => sum + b.in, 0) : null;
  // Early departures: people who left after the game started but before the main exit wave
  // (the busiest quarter-hour of exits, taken as the end of the game)
  const earlyExits = startMinute != null && exitWave && exitWave.start > startMinute
    ? flow.filter((b) => b.start >= startMinute && b.start < exitWave.start).reduce((sum, b) => sum + b.out, 0)
    : null;
  const dataStart = combined.length ? combined[0].minute : null;
  const dataEnd = combined.length ? combined[combined.length - 1].minute : null;

  return {
    hasData: combined.length > 0,
    startMinute,
    gatesOpenMinute,
    windowStart: range ? range.start : dataStart,
    windowEnd: range ? range.end : dataEnd,
    entrances,
    exits,
    rush,
    exitWave,
    peakGate,
    busiest: busiest && busiest.entrances > 0 ? busiest : null,
    avgQueue: queuePoints.length ? avg(queuePoints.map((p) => p.v)) : null,
    beforeStartShare: before != null && entrances ? pct(before, entrances) : null,
    earlyExits,
    earlyShare: earlyExits != null && entrances ? (earlyExits / entrances) * 100 : null,
    perGate,
    entriesPoints: flow.map((b) => ({ m: b.start, v: b.in })),
    exitsPoints: flow.map((b) => ({ m: b.start, v: b.out })),
    queuePoints,
  };
};

// "What stands out" across the selected games. cards: [{ date, label, color, m }]
export const buildGatesOverviewInsights = (cards) => {
  const withData = cards.filter((c) => c.m?.hasData);
  if (!withData.length) return [];
  const multi = withData.length >= 2;
  const n = withData.length;
  const insights = [];

  // Busiest gate: most entrances across all the selected games combined (the same order as the
  // Average column in "Gates across the games"), and how many games it led on its own
  const gateIds = Array.from(new Set(withData.flatMap((c) => c.m.perGate.map((g) => g.id))));
  const labelOf = (id) => withData.flatMap((c) => c.m.perGate).find((g) => g.id === id)?.label;
  const allEntrances = withData.reduce((sum, c) => sum + c.m.entrances, 0);
  const gateTotals = gateIds.map((id) => ({
    id,
    label: labelOf(id),
    entrances: withData.reduce((sum, c) => sum + (c.m.perGate.find((g) => g.id === id)?.entrances || 0), 0),
    led: withData.filter((c) => c.m.busiest?.id === id).length,
  })).sort((a, b) => b.entrances - a.entrances);
  const topGate = gateTotals[0];
  if (topGate && topGate.entrances > 0 && gateIds.length > 1) {
    const share = pct(topGate.entrances, allEntrances);
    // Gates that led the games this one didn't
    const otherLeaders = Array.from(new Set(
      withData.filter((c) => c.m.busiest && c.m.busiest.id !== topGate.id).map((c) => c.m.busiest.label)
    ));
    const names = otherLeaders.length <= 1 ? otherLeaders[0] : `${otherLeaders.slice(0, -1).join(', ')} and ${otherLeaders[otherLeaders.length - 1]}`;
    let text;
    if (!multi) {
      text = <><strong>{topGate.label}</strong> was the busiest gate, with <strong>{share}%</strong> of entries.</>;
    } else if (topGate.led === n) {
      text = <><strong>{topGate.label}</strong> was the busiest gate in <strong>every game</strong>, with <strong>{share}%</strong> of all entries.</>;
    } else if (topGate.led > 0) {
      text = (
        <>
          <strong>{topGate.label}</strong> had the most entries overall (<strong>{share}%</strong>) and was the busiest gate in
          {' '}<strong>{topGate.led} of {n}</strong> games{names ? <>; <strong>{names}</strong> led the others</> : ''}.
        </>
      );
    } else {
      text = (
        <>
          <strong>{topGate.label}</strong> had the most entries overall (<strong>{share}%</strong>) but didn&apos;t lead any single game:
          {' '}<strong>{names}</strong> {otherLeaders.length > 1 ? 'each edged it out' : 'edged it out each time'}.
        </>
      );
    }
    insights.push({ key: 'gate', tone: 'info', icon: 'door-open', score: 80, label: 'Busiest gate', text });
  }

  // Longest queues: the gate with the most time over 5 min across the games
  const queueTotals = gateIds.map((id) => {
    const entries = withData.map((c) => c.m.perGate.find((g) => g.id === id)).filter(Boolean);
    const peak = entries.reduce((top, g) => (g.queuePeak && (!top || g.queuePeak.value > top.value) ? g.queuePeak : top), null);
    return { id, label: entries[0]?.label, longMinutes: entries.reduce((sum, g) => sum + g.longMinutes, 0), games: entries.length || 1, peak };
  }).filter((q) => q.peak).sort((a, b) => b.longMinutes - a.longMinutes || b.peak.value - a.peak.value);
  const worstQueue = queueTotals[0];
  if (worstQueue) {
    const isLong = worstQueue.longMinutes > 0;
    insights.push({
      key: 'queues',
      tone: isLong ? 'bad' : 'good',
      icon: 'hourglass-split',
      score: isLong ? 95 : 40,
      label: isLong ? 'Longest queues' : 'Queues',
      text: isLong ? (
        <>
          <strong>{worstQueue.label}</strong> had the longest queues: at {QUEUE_TARGET} min or more for
          {' '}<strong>{formatDuration(multi ? worstQueue.longMinutes / worstQueue.games : worstQueue.longMinutes)}</strong>
          {multi ? ' per game on average' : ''}, peaking at <strong>{worstQueue.peak.value} min</strong>.
        </>
      ) : (
        <>Queues stayed short{multi ? ' in every game' : ''}: every gate stayed under {QUEUE_TARGET} min.</>
      ),
    });
  }

  // When the entry rush hits, relative to game start
  // Measured from the middle of each game's busiest quarter-hour
  const rushes = withData.filter((c) => c.m.rush && c.m.startMinute != null).map((c) => midOffset(c.m.rush, c.m.startMinute));
  if (rushes.length) {
    const lo = Math.min(...rushes);
    const hi = Math.max(...rushes);
    const steady = hi - lo <= 15;
    insights.push({
      key: 'rush',
      tone: 'info',
      icon: 'graph-up-arrow',
      score: 100,
      label: 'Entry rush',
      text: !multi ? (
        <>Entries peaked <strong>{relativeToStart(rushes[0])}</strong>.</>
      ) : steady ? (
        <>Entries peaked at the same point in every game: about <strong>{relativeToStart(avg(rushes))}</strong>.</>
      ) : (
        <>Entries peaked <strong>{offsetRange(lo, hi)}</strong>, depending on the game.</>
      ),
    });
  }

  // When people leave
  const waves = withData.filter((c) => c.m.exitWave && c.m.startMinute != null).map((c) => midOffset(c.m.exitWave, c.m.startMinute));
  if (waves.length) {
    insights.push({
      key: 'exits',
      tone: 'info',
      icon: 'box-arrow-right',
      score: 70,
      label: 'Exit wave',
      text: (
        <>
          Most people left about <strong>{relativeToStart(avg(waves))}</strong>
          {multi && waves.length > 1 && Math.max(...waves) - Math.min(...waves) > 15
            ? ` (${offsetRange(Math.min(...waves), Math.max(...waves))})`
            : multi && waves.length > 1 ? ' in every game' : ''}.
        </>
      ),
    });
  }

  // Early departures: which game had the most people leave before the main exit wave
  const early = withData
    .filter((c) => c.m.earlyShare != null)
    .sort((a, b) => b.m.earlyShare - a.m.earlyShare);
  if (early.length) {
    const top = early[0];
    const others = early.slice(1);
    const share = (c) => `${Math.round(c.m.earlyShare)}%`;
    insights.push({
      key: 'early',
      tone: 'info',
      icon: 'box-arrow-left',
      score: 75,
      label: 'Early departures',
      text: others.length ? (
        <>
          <strong>{top.label.long}</strong> had the most early departures: <strong>{top.m.earlyExits.toLocaleString()}</strong> people
          {' '}(<strong>{share(top)}</strong> of entrances) left after game start but before the main exit wave at {minuteToClock(top.m.exitWave.start)}.
          {' '}{others.map((c, i) => <React.Fragment key={c.date}>{i ? ', ' : 'Other games: '}{c.label.short} {share(c)}</React.Fragment>)}.
        </>
      ) : (
        <>
          <strong>{top.m.earlyExits.toLocaleString()}</strong> people (<strong>{share(top)}</strong> of entrances) left after game start
          {' '}but before the main exit wave at {minuteToClock(top.m.exitWave.start)}.
        </>
      ),
    });
  }

  return insights.sort((a, b) => b.score - a.score);
};

export { minuteToClock };
